import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCrown, faMedal } from "@fortawesome/free-solid-svg-icons";
import { formatMubouScore } from "../../utils/scoreDisplay";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const styles = {
  page: {
    minHeight: "80vh",
    background:
      "radial-gradient(circle at top left, rgba(255, 215, 0, 0.16), transparent 34%), radial-gradient(circle at bottom right, rgba(255, 140, 0, 0.12), transparent 30%), linear-gradient(160deg, #0a0a1a 0%, #1a1035 52%, #0d0d2b 100%)",
    padding: "40px 16px 64px",
  },
  container: {
    maxWidth: 760,
    margin: "0 auto",
  },
  titleArea: {
    textAlign: "center",
    marginBottom: 34,
  },
  title: {
    fontSize: "clamp(1.45rem, 5vw, 2.25rem)",
    fontWeight: 900,
    letterSpacing: "0.03em",
    color: "#ffd700",
    margin: "0 0 10px",
  },
  subText: {
    fontSize: "clamp(0.9rem, 2.6vw, 1rem)",
    color: "rgba(255, 255, 255, 0.62)",
    lineHeight: 1.8,
    margin: 0,
  },
  loadingText: {
    textAlign: "center",
    padding: "42px 20px",
    color: "rgba(255, 255, 255, 0.56)",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
  },
  emptyIconWrap: {
    width: 80,
    height: 80,
    borderRadius: "50%",
    background: "rgba(255, 215, 0, 0.08)",
    border: "1px solid rgba(255, 215, 0, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
  },
  emptyTitle: {
    fontSize: "clamp(1.12rem, 3.5vw, 1.5rem)",
    color: "rgba(255, 255, 255, 0.82)",
    margin: "0 0 12px",
    fontWeight: 800,
  },
  emptyText: {
    fontSize: "clamp(0.86rem, 2.5vw, 1rem)",
    color: "rgba(255, 255, 255, 0.5)",
    lineHeight: 1.9,
    maxWidth: 430,
    margin: "0 auto",
  },
};

function getCardStyle(rank) {
  const backgrounds = {
    1: "linear-gradient(135deg, rgba(255,215,0,0.26), rgba(255,180,0,0.08))",
    2: "linear-gradient(135deg, rgba(216,220,230,0.20), rgba(255,255,255,0.06))",
    3: "linear-gradient(135deg, rgba(205,127,50,0.22), rgba(255,170,80,0.06))",
  };
  const borders = {
    1: "1px solid rgba(255,215,0,0.42)",
    2: "1px solid rgba(216,220,230,0.26)",
    3: "1px solid rgba(205,127,50,0.28)",
  };

  return {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 18px",
    marginBottom: 12,
    borderRadius: 18,
    cursor: "pointer",
    textAlign: "left",
    background: backgrounds[rank] || "rgba(255,255,255,0.045)",
    border: borders[rank] || "1px solid rgba(255,255,255,0.09)",
  };
}

function getBadgeStyle(rank) {
  const colors = {
    1: "#ffd700",
    2: "#d8dce6",
    3: "#cd7f32",
  };

  return {
    width: 40,
    minWidth: 40,
    height: 40,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: rank <= 3 ? "1.2rem" : "0.95rem",
    fontWeight: 900,
    color: colors[rank] || "rgba(255,255,255,0.62)",
    background: "rgba(255, 255, 255, 0.06)",
  };
}

function getStatusStyle(goal) {
  if (goal.achievement_verified) {
    return { label: "運営認証済み", background: "#dff7e4", color: "#145b23" };
  }
  if (goal.achievement_verification_requested) {
    return { label: "認証申請中", background: "#fff1bf", color: "#7a4b00" };
  }
  return { label: "自己申告", background: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.72)" };
}

function formatScore(goal) {
  return formatMubouScore(goal);
}

function formatDate(dateStr) {
  if (!dateStr) return "日付不明";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "日付不明";
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
}

function getRankIcon(index) {
  if (index === 0) return <FontAwesomeIcon icon={faCrown} />;
  if (index <= 2) return <FontAwesomeIcon icon={faMedal} />;
  return index + 1;
}

function AchievedRankingPage() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAchieved = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/goals/achieved/`);
        setGoals(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Failed to fetch achieved goals:", error);
        setGoals([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAchieved();
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.titleArea}>
          <h1 style={styles.title}>達成者ランキング</h1>
          <p style={styles.subText}>
            無謀な夢を、ほんとうに現実へ引きずり出した人たち。
          </p>
        </div>

        {loading ? (
          <div style={styles.loadingText}>読み込み中...</div>
        ) : goals.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIconWrap}>
              <FontAwesomeIcon icon={faCrown} color="rgba(255,215,0,0.7)" size="2x" />
            </div>
            <h2 style={styles.emptyTitle}>まだ誰も、ここまで来ていません</h2>
            <p style={styles.emptyText}>
              この場所に名前を刻む最初のひとりは、まだ現れていません。
              <br />
              でも、無謀な夢はだいたいこういう静かなところから始まります。
            </p>
          </div>
        ) : (
          goals.map((goal, index) => {
            const rank = index + 1;
            const status = getStatusStyle(goal);
            return (
              <button
                key={goal.id}
                type="button"
                style={getCardStyle(rank)}
                onClick={() => navigate(`/boards/${goal.id}`)}
              >
                <span style={getBadgeStyle(rank)}>{getRankIcon(index)}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      fontWeight: 800,
                      fontSize: "clamp(0.94rem, 2.7vw, 1.08rem)",
                      color: "#fff",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {goal.title}
                    <span
                      style={{
                        display: "inline-block",
                        background: "linear-gradient(135deg, #ffd700, #ff9f1c)",
                        color: "#1a1324",
                        fontSize: "0.7rem",
                        fontWeight: 900,
                        padding: "2px 8px",
                        borderRadius: 999,
                        marginLeft: 8,
                        verticalAlign: "middle",
                      }}
                    >
                      達成
                    </span>
                    <span
                      style={{
                        display: "inline-block",
                        background: status.background,
                        color: status.color,
                        fontSize: "0.68rem",
                        fontWeight: 900,
                        padding: "2px 8px",
                        borderRadius: 999,
                        marginLeft: 6,
                        verticalAlign: "middle",
                      }}
                    >
                      {status.label}
                    </span>
                  </span>
                  <span
                    style={{
                      display: "block",
                      fontSize: "clamp(0.76rem, 2.2vw, 0.86rem)",
                      color: "rgba(255, 255, 255, 0.56)",
                      marginTop: 4,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {goal.user && goal.user.username ? goal.user.username : "匿名"} ・ 達成日:{" "}
                    {formatDate(goal.achieved_at)}
                  </span>
                </span>
                <span
                  style={{
                    fontWeight: 900,
                    fontSize: "clamp(0.82rem, 2.4vw, 0.96rem)",
                    color: "#ffd700",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {formatScore(goal)}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export default AchievedRankingPage;

